import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import useAuthStore from "../../../services/AuthStore";
import { Delete, Edit } from "@mui/icons-material";

export default function Users() {
  const { getUsers, getRoles, register, updateUser } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    id: null,
    username: "",
    password: "",
    roles: [],
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    getUsers()
      .then(newUsers => {
        setUsers(newUsers);
      })
      .catch(error => {
        console.error("Ошибка при получении пользователей:", error);
      });
  }, [getUsers]);

  useEffect(() => {
    getRoles()
      .then(newRoles => {
        setRoles(newRoles);
      })
      .catch(error => {
        console.error("Ошибка при получении ролей:", error);
      });
  }, [getRoles]);

  const handleOpenDialog = (
    user = { id: null, username: "", password: "", roles: [] },
  ) => {
    setCurrentUser(user);
    setIsEditing(!!user.id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentUser({ id: null, username: "", password: "", roles: [] });
  };

  const handleSaveUser = () => {
    if (isEditing) {
      setUsers(prev =>
        prev.map(user => (user.id === currentUser.id ? currentUser : user)),
      );
    } else {
      register(
        currentUser.username,
        currentUser.password,
        currentUser.roles.map(role => role.id),
      );
    }
    handleCloseDialog();
  };

  const handleDeleteUser = id => {
    setUsers(prev => prev.filter(user => user.id !== id));
  };

  const columns = [
    { field: "id", headerName: "Номер", width: 90 },
    { field: "username", headerName: "Имя пользователя", width: 150 },
    {
      field: "roles",
      headerName: "Роли",
      width: 200,
      renderCell: params => {
        const { roles } = params.row;
        if (roles) {
          return roles.map(role => role.name).join(", ");
        }
        return "Не заданы роли";
      },
    },
    {
      field: "actions",
      headerName: "Действия",
      width: 200,
      renderCell: params => (
        <Box>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleOpenDialog(params.row)}>
            <Edit />
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={() => handleDeleteUser(params.row.id)}
            style={{ marginLeft: 8 }}>
            <Delete />
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h6" style={{ marginBottom: 16 }}>
        Управление пользователями
      </Typography>
      <Button
        variant="contained"
        onClick={() => handleOpenDialog()}
        style={{ marginBottom: 16 }}>
        Добавить нового пользователя
      </Button>
      <Box sx={{ height: 400, width: "100%" }}>
        <DataGrid
          rows={users}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
        />
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>{isEditing ? "Edit User" : "Add User"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Username"
            type="text"
            fullWidth
            value={currentUser.username}
            onChange={e =>
              setCurrentUser({ ...currentUser, username: e.target.value })
            }
          />
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type="text"
            fullWidth
            value={currentUser.password}
            onChange={e =>
              setCurrentUser({ ...currentUser, password: e.target.value })
            }
          />
          <Select
            multiple
            fullWidth
            value={currentUser.roles.map(role => role.id)} // Получаем массив id ролей
            onChange={e => {
              const selectedIds = e.target.value;
              const selectedRoles = roles.filter(role =>
                selectedIds.includes(role.id),
              );
              setCurrentUser({ ...currentUser, roles: selectedRoles });
            }}
            renderValue={selected =>
              selected
                .map(id => roles.find(role => role.id === id)?.name)
                .join(", ")
            }>
            {roles.map(role => (
              <MenuItem key={role.id} value={role.id}>
                <Checkbox
                  checked={currentUser.roles.some(
                    userRole => userRole.id === role.id,
                  )}
                />
                <ListItemText primary={role.name} />
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
